/**
 * The form block: the one place a person types something into a panel.
 *
 * It is here rather than in blocks.tsx because it is the only renderer with
 * something behind it -- what has been typed and not yet submitted, whether a
 * save confirmed it, and what happens to the typing when the content moves --
 * and because one of its controls (`type: "list"`, CHARTER §7.113) is a small
 * table with its own add and remove.
 *
 * None of those three questions is answered here. They are `surfacekit/form.js`,
 * the state machine a plugin's own page runs inside the sandbox, reached through
 * `formMachine.ts`: `liquid-page-runtime.md` §5 says the shell's settings and
 * request forms are the same behaviour as a page's forms, and one behaviour
 * written twice is two behaviours. What this file decides is what a page decides
 * -- which declared read carries the content, where its version is, which keys
 * are this form's, and what the person is shown.
 *
 * A form's buttons are the block's `actions`; there is no separate `submit`.
 * What each one sends is `argument_fields` picked out of what was typed, plus
 * its own `constants`, optionally packed under `pack_fields_as` -- assembled on
 * the server, because the shell hands over values, not arguments.
 *
 * Two rules run through it. A secret is write-only in both directions: never
 * populated from stored data, never in a draft, never part of the content a
 * version is about, and gone from the page once it has been sent -- which is why
 * it is held beside the machine rather than in it. And nothing is submitted
 * until every required field has something in it, with the refusal naming which
 * ones: a form that silently posts a half-filled object is how a setting ends up
 * half-written.
 */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { Accordion, Checkbox, NumberField, SearchSelect, Select } from "../components/rumonai";
import { FormDraftScope } from "./InteractionFrame";
import { changedFormValues, ordinaryForm } from "../connections/api";
import { ChoiceOrOwnWords } from "./choiceOrOwn";
import { commandSubmission, directSubmission, useFormMachine, type FormRead } from "./formMachine";
import { pluck, type PanelAction, type PanelField } from "./spec";
import type { SourceResult } from "../lib/api";
import type { BlockProps } from "./blocks";
import { ActionButton, BlockShell } from "./blocks";

/**
 * A form that is a question put to the Owner (`metadata.interaction`), where it is drawn: the ways of not
 * answering it stand at the left of its answer button, and each box says which question of how many it is.
 * Nothing else about the form changes.
 */
export const FormAsking = createContext<{ escapes: ReactNode } | null>(null);
const FormDisabled = createContext(false);

/** A presentation override; the shared form still owns values, drafts and submission. */
export interface FieldRendererProps {
  field: PanelField;
  id: string;
  value: unknown;
  onChange: (value: unknown) => void;
  describedBy?: string;
  disabled?: boolean;
}

/**
 * A setting that is a table -- one choice per name -- drawn as rows.
 *
 * Presentation only, like `primaryListFields`: the field stays whatever the
 * declaration said it is and its value stays the object the plugin reads. What
 * this adds is the words, and they are all the declaring plugin's: which
 * answers a row may give, and what each row and each answer is called.
 */
export interface ChoiceMap {
  /** What a row may say, in the declared order. */
  choices: { value: string; label: string }[];
  /** Rows that are always on the page, set or not. */
  rows: { key: string; label: string }[];
  /** Names a person may add a row for. Absent: `rows` is the whole table. */
  options?: { key: string; label: string }[];
}

/**
 * A setting that is a set of values the plugin offers, drawn as checkboxes.
 *
 * Presentation only, like `ChoiceMap`: the field stays a list and its value
 * stays the list the plugin reads. The words are the declaring plugin's --
 * what each value is called -- and so is `ordered`, which says its code reads
 * the list in order.
 */
export interface CheckList {
  /** Every value on offer, in the declared order, each by its name. */
  options: { value: string; label: string }[];
  /** The checked ones can be moved, and are stored in the order shown. */
  ordered: boolean;
}

/** The option standing for a stored answer no declared choice names. Never a real answer. */
const UNLISTED = "\u0000";

/** The table as stored, or as a draft from when this field was still JSON text. */
function choiceMapValue(value: unknown): Record<string, unknown> {
  let found = value;
  if (typeof found === "string" && found.trim()) {
    try { found = JSON.parse(found); } catch { return {}; }
  }
  return found && typeof found === "object" && !Array.isArray(found)
    ? found as Record<string, unknown> : {};
}

/** What a field holds before anything is typed into it. */
function emptyValue(field: PanelField): unknown {
  // A secret starts empty whatever the declaration says: a default here would
  // be a secret written down in a registry row.
  if (field.secret) return "";
  if (field.type === "list") return [];
  if (field.default != null) return field.default;
  if (field.type === "boolean") return field.required ? false : undefined;
  return "";
}

function emptyRow(fields: PanelField[]): Record<string, unknown> {
  return Object.fromEntries(fields.map((f) => [f.key, emptyValue(f)]));
}

/** A value a person has not supplied. `false` is an answer; an empty string is not. */
function blank(field: PanelField, value: unknown): boolean {
  if (field.type === "list") return !Array.isArray(value) || value.length === 0;
  // Spaces alone are not an answer a person wrote in their own words.
  if (field.type === "choice" && field.writable && typeof value === "string") return value.trim() === "";
  return value === "" || value === undefined || value === null;
}

function missingFields(
  fields: PanelField[],
  values: Record<string, unknown>,
  prefix = "",
): string[] {
  return fields.flatMap((field) => {
    const value = values[field.key];
    const label = `${prefix}${field.label}`;
    if (field.required && blank(field, value)) return [label];
    if (field.item_fields && Array.isArray(value))
      return value.flatMap((row, index) =>
        missingFields(
          field.item_fields!,
          row as Record<string, unknown>,
          `${label} 第 ${index + 1} 行 · `,
        ),
      );
    return [];
  });
}

/**
 * This form's projected state, in the shape the machine reads.
 *
 * Which read, where in it, and where its version is are all the declaration's
 * business -- `block.source_index`, `block.body`, and `PanelSource.version` as
 * the server resolved it. A caller that already holds the content and its
 * version (the settings page, which reads a namespace rather than a panel) hands
 * them straight over as `content`.
 *
 * `version` absent and `version` null are different answers and stay different
 * all the way through: no declared version means nobody can tell whether this
 * moved, which the machine reports as `versioned: false` so that the page can
 * say so instead of overwriting (runtime §3).
 */
function formContent(
  block: BlockProps["block"],
  reads: SourceResult[],
  content: { value: Record<string, unknown>; version?: unknown } | undefined,
  secrets: string[],
): FormRead | null {
  const strip = (value: unknown) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return value;
    const out = { ...(value as Record<string, unknown>) };
    // Never populated from stored data, whatever a read answered with.
    for (const key of secrets) delete out[key];
    return out;
  };
  if (content) {
    return { state: "ready", value: strip(content.value),
      ...("version" in content ? { version: content.version } : {}) };
  }
  if (!block.body) return null;
  const source = reads[block.source_index ?? 0];
  const found = source ? pluck(source.value ?? source.rows?.[0], block.body) : undefined;
  const ready = source?.state === "ready" && Boolean(found)
    && typeof found === "object" && !Array.isArray(found);
  return {
    state: ready ? "ready" : source?.state === "error" ? "error" : "pending",
    ...(ready ? { value: strip(found) } : {}),
    ...(source?.error ? { error: source.error } : {}),
    ...(source && "version" in source ? { version: source.version } : {}),
  };
}

export function FormBlockView({
  block,
  index,
  runAction,
  busy,
  initialValues,
  version,
  reads,
  idPrefix,
  primaryListFields,
  choiceMaps,
  checkLists,
  fieldRenderers,
  focusField,
  onFocused,
  write,
}: BlockProps & {
  initialValues?: Record<string, unknown>;
  /**
   * The version `initialValues` was read at, for a caller that read the content
   * itself. Left out entirely when there is none -- see `formContent`.
   */
  version?: unknown;
  idPrefix?: string;
  /** Presentation only: all list fields keep the same values, validation and submission. */
  primaryListFields?: Record<string, string[]>;
  /** Presentation only: which fields are a table of choices, and the words for it. */
  choiceMaps?: Record<string, ChoiceMap>;
  /** Presentation only: which list fields are a set of named values. */
  checkLists?: Record<string, CheckList>;
  fieldRenderers?: Record<string, (props: FieldRendererProps) => ReactNode>;
  /** Reveal a field selected in settings search, including a nested list column. */
  focusField?: string[];
  /** Told once `focusField` has been revealed, so whoever asked can let it go. */
  onFocused?: () => void;
  /**
   * A save that is a direct write, not a command the core follows (ruling 204:
   * the owner's own settings). The press carries the version the content was
   * read at as `expected_generation`, and settles on the write's own answer.
   * `runAction` still serves whatever else the block declares.
   */
  write?: (action: PanelAction, params: Record<string, unknown>, id: string) => Promise<void>;
}) {
  const prefix = idPrefix ?? `b${index}`;
  const fields = block.fields ?? [];
  const fieldset = useRef<HTMLFieldSetElement>(null);
  const actions = block.actions ?? [];
  const draftScope = useContext(FormDraftScope);
  const asking = useContext(FormAsking);
  const draftName = draftScope ? `panel-form:${draftScope}:${index}` : "";
  const secretKeys = fields.filter((field) => field.secret).map((field) => field.key);
  const content = formContent(block, reads,
    initialValues === undefined ? undefined
      : { value: initialValues, ...(version !== undefined ? { version } : {}) },
    secretKeys);
  const [options] = useState(() => ({
    read: 0,
    draft: draftName,
    fields: fields.filter((field) => !field.secret).map((field) => field.key),
    ...(write ? { version_field: "expected_generation" } : {}),
    // A form with nothing read behind it is not a form waiting for a read: a
    // question put to the owner has no business content coming, and waiting for one
    // would leave its boxes disabled forever.
    ...(content ? {} : { bound: false, initial: emptyRow(fields.filter((field) => !field.secret)) }),
  }));
  const [form, machine] = useFormMachine(options, content ? [content] : [], {
    redact: (value) => ordinaryForm(fields, (value || {}) as Record<string, unknown>).values,
  });
  // Held beside the machine and never in it: a secret is not content, so it is
  // not what a version is about and not what a draft keeps. It rides along on
  // the submission and then leaves the page.
  const [secrets, setSecrets] = useState<Record<string, unknown>>({});
  const valuesAt = (current: unknown) => {
    const typed = (current || {}) as Record<string, unknown>;
    return Object.fromEntries(fields.map((field) => [field.key,
      field.secret ? (secrets[field.key] ?? "") : (typed[field.key] ?? emptyValue(field))]));
  };
  const values = valuesAt(form.current);
  const answered = fields.filter(field => !blank(field, values[field.key])).length;
  const waiting = Boolean(content) && form.state !== "ready";
  const [refused, setRefused] = useState(false);
  // Whether a save has been attempted from this form. The machine's `error`
  // outlives whatever set it -- a read that failed once sets it too -- so the
  // line below is only ever the form's account of the last press.
  const [tried, setTried] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState<string[]>([]);

  // What a press answers for. A command carries the whole form, so every
  // required field has to be filled. A direct write carries only what changed
  // (`write`), so it answers for the changed fields: a box nobody touched is not
  // this save's to refuse, whatever it is marked -- the plugin's own readiness
  // line says what is still missing, and each box saves on its own terms.
  const answerable = (current: Record<string, unknown>) => write
    ? fields.filter((field) => field.key in changedFormValues([field], current, initialValues ?? {}))
    : fields;

  // Derived, not remembered. A stored list goes stale the moment the person
  // fills the field in: the form said 「清理规则 还没填」 above two filled-in
  // rows until the next press, which is the page arguing with what is on it.
  const missing = refused ? missingFields(answerable(values), values) : [];

  const set = (key: string, value: unknown) => {
    if (secretKeys.includes(key)) setSecrets((prev) => ({ ...prev, [key]: value }));
    else machine.set({ [key]: value });
  };

  const submit = (actionIndex: number) => {
    const action = actions[actionIndex];
    // Validate the state submit will capture. React may not have rendered a
    // just-added row yet, while the machine already holds its empty fields.
    const current = valuesAt(machine.current);
    const blanks = missingFields(answerable(current), current);
    setRefused(blanks.length > 0);
    if (blanks.length) return Promise.resolve();
    setTried(true);
    // The machine captures what is on the form now and confirms that and no
    // other version of it; the secrets ride along without ever having been part
    // of it.
    const id = `${index}:${actionIndex}`;
    const sent = machine.submit(
      write
        ? directSubmission((params) => write(action, params, id))
        : commandSubmission((params) => runAction(action, params, id)),
      secrets,
    ).then(() => undefined, () => undefined);
    // A secret is write-only in both directions: it is never read back from the
    // core, and it does not stay on this page after it has been sent. The rest
    // of the form stays, so a person can see what they just submitted while the
    // trail says what became of it.
    if (secretKeys.length) setSecrets({});
    return sent;
  };

  const focused = useRef(onFocused);
  focused.current = onFocused;
  useEffect(() => {
    const root = fieldset.current;
    if (!focusField?.length || waiting || !root) return;
    let target: HTMLElement = root;
    for (const key of focusField) {
      // A field of this level, not a list column one level down that happens
      // to share its key.
      const found = [...target.querySelectorAll<HTMLElement>("[data-field-key]")].find(element => {
        const owner = element.parentElement?.closest<HTMLElement>("[data-field-key]");
        return element.dataset.fieldKey === key && (owner && root.contains(owner) ? owner : root) === target;
      });
      if (!found) break; // An empty list has no row yet: reveal its add control.
      target = found;
    }
    if (target === root) return;
    let opening = false;
    for (let parent = target.parentElement; parent && root.contains(parent); parent = parent.parentElement) {
      if (!parent.matches('[role="region"][hidden]')) continue;
      const trigger = [...root.querySelectorAll<HTMLButtonElement>("button")]
        .find(button => button.id === parent.getAttribute("aria-labelledby") || button.getAttribute("aria-controls") === parent.id);
      if (trigger) { trigger.click(); opening = true; }
    }
    const focus = () => {
      const labelled = target.querySelector<HTMLLabelElement>(":scope > .field-label")?.control;
      (labelled ?? target.querySelector<HTMLElement>("input:not([type='hidden']), textarea, button"))?.focus({ preventScroll: true });
      target.scrollIntoView({ block: "center" });
      // A later reload must not pull the Owner back here.
      focused.current?.();
    };
    if (opening) {
      const frame = requestAnimationFrame(focus);
      return () => cancelAnimationFrame(frame);
    }
    focus();
  }, [focusField, waiting]);

  return (
    <BlockShell block={block} index={index}>
      {waiting ? <p role="status">{form.state === "error"
        ? form.error || "表单内容尚未读到。"
        : "表单内容尚未读到，已填写的草稿仍保留。"}</p> : null}
      <FormDisabled.Provider value={waiting}><fieldset ref={fieldset} className={asking ? "form question-form" : "form"} disabled={waiting} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        {asking && fields.length > 1 ? <div className="question-progress">
          <div><span>{fields.length} 个问题</span><span>已填写 {answered} / {fields.length}</span></div>
          <progress aria-label="回答填写进度" value={answered} max={fields.length} />
        </div> : null}
        {form.persisted ? null : <p role="alert">草稿暂时无法保存，请不要关闭这一页。</p>}
        {/* A read whose declaration named no version cannot be checked against
            anybody else's edit, and §3 says the form has to admit that rather
            than behave as though it had checked. Only once something has been
            typed: before that there is nothing that could be lost. */}
        {content && !form.versioned && form.dirty
          ? <p className="micro">这一项没有说它的版本，所以别处同时改过的话，这一页看不出来。</p> : null}
        {[fields.filter(field => !field.advanced || field.required),
          fields.filter(field => field.advanced && !field.required)].map((group, groupIndex) => {
          const controls = group.map((field) => (
          <div className={`form-field${["choice", "number", "boolean"].includes(field.type ?? "") && !field.writable ? " form-field-short" : ""}`} key={field.key} data-field-key={field.key}>
            <label
              className="field-label micro"
              htmlFor={`${prefix}-${field.key}`}
            >
              {asking && fields.length > 1 ? <span className="question-number" aria-hidden="true">{fields.indexOf(field) + 1}</span> : null}
              {field.label}
              {field.required ? (
                <span className="req" aria-hidden="true">
                  {" "}
                  *
                </span>
              ) : null}
              {asking && !field.required ? <span className="question-optional" aria-hidden="true">选填</span> : null}
            </label>
            {fieldRenderers?.[field.key] ? fieldRenderers[field.key]({
              field, id: `${prefix}-${field.key}`, value: values[field.key],
              onChange: (value) => set(field.key, value),
              describedBy: field.placeholder ? `${prefix}-${field.key}-help` : undefined,
              disabled: waiting,
            }) : choiceMaps?.[field.key] ? (
              <ChoiceMapField
                field={field}
                map={choiceMaps[field.key]}
                idPrefix={`${prefix}-${field.key}`}
                value={choiceMapValue(values[field.key])}
                onChange={(table) => set(field.key, table)}
                describedBy={field.placeholder ? `${prefix}-${field.key}-help` : undefined}
              />
            ) : checkLists?.[field.key] ? (
              <CheckListField
                field={field}
                list={checkLists[field.key]}
                value={Array.isArray(values[field.key]) ? values[field.key] as string[] : []}
                onChange={(next) => set(field.key, next)}
                describedBy={field.placeholder ? `${prefix}-${field.key}-help` : undefined}
              />
            ) : field.type === "list" ? (
              <ListField
                field={field}
                idPrefix={`${prefix}-${field.key}`}
                rows={
                  Array.isArray(values[field.key])
                    ? (values[field.key] as unknown[])
                    : []
                }
                onChange={(rows) => set(field.key, rows)}
                primaryKeys={primaryListFields?.[field.key]}
                describedBy={field.secret || field.placeholder ? `${prefix}-${field.key}-help` : undefined}
              />
            ) : (
              <FieldControl
                field={field}
                id={`${prefix}-${field.key}`}
                value={values[field.key]}
                onChange={(v) => set(field.key, v)}
                describedBy={field.secret || field.placeholder ? `${prefix}-${field.key}-help` : undefined}
              />
            )}
            {field.secret ? (
              <p className="field-help micro" id={`${prefix}-${field.key}-help`}>
                只写不回显：存进去之后这里也读不出来，要换就重填一次。
              </p>
            ) : field.placeholder ? <p className="field-help micro" id={`${prefix}-${field.key}-help`}>{field.placeholder}</p> : null}
          </div>
          ));
          return groupIndex === 0 ? controls : group.length ? <Accordion className="list-advanced" key="advanced" forceMount
            value={advancedOpen} onValueChange={setAdvancedOpen} items={[{ value: "advanced", title: `高级设置（${group.length}）`,
              content: <div className="advanced-fields">{controls}</div> }]} /> : null;
        })}

        {missing.length ? (
          <p className="form-error" role="alert">
            没有提交：{missing.join("、")} 还没填。填完再按一次。
          </p>
        ) : null}
        {/* What became of the last press, beside the button that made it: on
            a long form a line at the top is off the screen the person is
            looking at when they press 保存. */}
        {tried && form.error && !form.saving ? <p className="form-error" role="alert">{form.error}</p> : null}
        {/* A press that went through says so here.  The card it leaves behind may land in the conversation that
            owns the work, not the one in front of the person (walk W13: 补充要求 sent, nothing on the page said
            so).  A direct write is the settings page's, which says 已保存 itself. */}
        {!write && tried && form.saved && !form.error ? <p className="micro" role="status">已送出</p> : null}

        <div className="form-actions">
          {asking?.escapes}
          {actions.length ? (
            actions.map((action, i) => (
              <ActionButton
                key={action.key || i}
                id={`${index}:${i}`}
                action={action}
                values={values}
                runAction={() => submit(i)}
                busy={busy}
              />
            ))
          ) : (
            <p className="micro faint">
              这个表单没有声明按钮，填了也交不出去。
            </p>
          )}
        </div>
      </fieldset></FormDisabled.Provider>
    </BlockShell>
  );
}

/**
 * One control. The same component draws a top-level field and a cell inside a
 * list row, so the two cannot drift -- a secret column in a table has to be as
 * write-only as a secret field on its own.
 */
function FieldControl({
  field,
  id,
  value,
  onChange,
  describedBy,
}: {
  field: PanelField;
  id: string;
  value: unknown;
  onChange: (value: unknown) => void;
  describedBy?: string;
}) {
  const disabled = useContext(FormDisabled);
  if (field.type === "text") {
    return (
      <span className="pa-field-shell"><textarea
        id={id}
        name={field.key}
        disabled={disabled}
        rows={3}
        placeholder={field.secret ? field.placeholder : undefined}
        aria-describedby={describedBy}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
      /><span className="pa-paint-edge" aria-hidden="true" /></span>
    );
  }
  if (field.type === "choice" && field.writable) {
    return <ChoiceOrOwnWords field={field} id={id} value={value} onChange={onChange} describedBy={describedBy} disabled={disabled} />;
  }
  if (field.type === "choice") {
    const current = String(value ?? "");
    const choices = field.choices ?? [];
    const invalid = current !== "" && !choices.includes(current);
    const unavailable = choices.length === 0;
    // Nothing chosen is on the list only where it is an answer: the
    // declaration listed "" among its choices, under its own name. Anywhere
    // else it is a prompt while nothing is stored, and cannot be picked -- a
    // save could not carry it, so offering it was a button that did nothing.
    return (
      <>
      <Select
        id={id}
        name={field.key}
        disabled={disabled || unavailable}
        label={field.label}
        value={current}
        aria-invalid={invalid && !unavailable || undefined}
        aria-describedby={[describedBy, unavailable ? `${id}-unavailable` : invalid ? `${id}-invalid` : undefined].filter(Boolean).join(" ") || undefined}
        onValueChange={onChange}
        items={[
          ...(invalid ? [{ value: current, label: unavailable ? `${field.choice_labels?.[current] ?? current}（已保存）` : "当前值不在可选范围", disabled: true }] : []),
          ...choices.map(choice => ({ value: choice, label: field.choice_labels?.[choice] ?? choice })),
        ]}
      />
      {unavailable ? <p className="field-help" id={`${id}-unavailable`}>
        暂时没有可选项。{current ? "已保存的值会保留。" : "可用后再选择。"}
      </p> : invalid ? <p className="form-error" id={`${id}-invalid`}>
        当前保存的选项已不在可选范围；未修改时保留原值，修改时请重新选择。
      </p> : null}
      </>
    );
  }
  if (field.type === "boolean") {
    // Without a declared default, absence may mean a provider-selected value.
    // Keep that choice distinct from an explicit false.
    const inherited = typeof value !== "boolean";
    return (
      <>
        <Checkbox
          id={id}
          name={field.key}
          disabled={disabled}
          label=""
          className="form-boolean-control"
          checked={inherited ? "indeterminate" : value === true}
          aria-describedby={describedBy}
          onCheckedChange={(next) => onChange(next === true)}
        />
        {inherited ? <span className="micro">使用服务默认值</span> : null}
      </>
    );
  }
  if (field.secret) {
    // Write-only, and it says so. Nothing is ever drawn into it: the core does
    // not send a stored secret back, and a masked placeholder standing in for
    // one would be a lie about what pressing 保存 submits.
    return (
      <span className="pa-field-shell"><input
        id={id}
        name={field.key}
        disabled={disabled}
        type="password"
        autoComplete="new-password"
        spellCheck={false}
        placeholder={field.placeholder || "留空就是不改"}
        aria-describedby={describedBy}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
      /><span className="pa-paint-edge" aria-hidden="true" /></span>
    );
  }
  if (field.type === "number") {
    return <NumberField id={id} name={field.key} label={field.label} aria-describedby={describedBy} disabled={disabled}
      min={field.minimum ?? undefined} max={field.maximum ?? undefined} value={String(value ?? "")}
      onValueChange={(next) => onChange(next ?? "")} />;
  }
  return (
    <span className="pa-field-shell"><input
      id={id}
      name={field.key}
      disabled={disabled}
      type="text"
      aria-describedby={describedBy}
      min={field.minimum ?? undefined}
      max={field.maximum ?? undefined}
      pattern={field.pattern || undefined}
      value={String(value ?? "")}
      onChange={(e) => onChange(e.target.value)}
    /><span className="pa-paint-edge" aria-hidden="true" /></span>
  );
}

/**
 * A table of one choice per name, as rows: a name, and what it is set to.
 *
 * Two kinds of row. The ones the declaration listed are always here, and 「未设置」
 * on one of them takes its name out of the table -- absent and set are different
 * answers, and which one the plugin treats as its default is the plugin's to
 * say in the field's own help line. Every other name in the table is a row
 * somebody added, so it has a button that takes it away instead.
 *
 * A stored answer the declaration does not list stays what it is until the
 * person picks another: this control never rewrites what it cannot say.
 */
function ChoiceMapField({
  field,
  map,
  idPrefix,
  value,
  onChange,
  describedBy,
}: {
  field: PanelField;
  map: ChoiceMap;
  idPrefix: string;
  value: Record<string, unknown>;
  onChange: (table: Record<string, unknown>) => void;
  describedBy?: string;
}) {
  const disabled = useContext(FormDisabled);
  const [adding, setAdding] = useState("");
  const listed = new Set(map.rows.map((row) => row.key));
  const named = new Map((map.options ?? []).map((option) => [option.key, option.label]));
  const added = Object.keys(value).filter((key) => !listed.has(key))
    .map((key) => ({ key, label: named.get(key) ?? key }));
  const open = (map.options ?? []).filter((option) => !(option.key in value) && !listed.has(option.key));
  const chosen = open.find((option) => option.key === adding);
  const put = (key: string, answer: string) => {
    const next = { ...value };
    if (answer) next[key] = answer;
    else delete next[key];
    onChange(next);
  };
  const add = () => {
    if (!chosen || !map.choices.length) return;
    put(chosen.key, map.choices[0].value);
    setAdding("");
  };
  const row = (item: { key: string; label: string }, removable: boolean) => {
    const current = typeof value[item.key] === "string" ? value[item.key] as string : "";
    const unknown = item.key in value && !map.choices.some((choice) => choice.value === current);
    const id = `${idPrefix}-${item.key}`;
    return (
      <li className="choice-map-row" key={item.key}>
        <label className="choice-map-name" htmlFor={id}>{item.label}</label>
        <Select id={id} label={item.label} disabled={disabled}
          value={unknown ? UNLISTED : current} aria-describedby={describedBy}
          onValueChange={(next) => put(item.key, next)}
          items={[
            ...(removable ? [] : [{ value: "", label: "未设置" }]),
            ...(unknown ? [{ value: UNLISTED, label: "当前值不在可选范围", disabled: true }] : []),
            ...map.choices,
          ]} />
        {removable ? (
          <button type="button" className="btn btn-quiet" aria-label={`移除 ${item.label}`}
            onClick={() => put(item.key, "")}>移除</button>
        ) : null}
      </li>
    );
  };
  return (
    <div className="choice-map" data-testid={`choice-map-${field.key}`} role="group" aria-label={field.label}>
      {map.rows.length || added.length ? (
        <ul className="choice-map-rows">
          {map.rows.map((item) => row(item, false))}
          {added.map((item) => row(item, true))}
        </ul>
      ) : <p className="micro faint">还没有一项。</p>}
      {map.options ? (
        <div className="choice-map-add">
          <SearchSelect id={`${idPrefix}-add`} label={`${field.label}：要添加的名称`}
            value={adding} placeholder="输入名称查找" onValueChange={setAdding} disabled={disabled}
            items={open.map(option => ({ value: option.key, label: option.label }))} />
          <button type="button" className="btn" disabled={disabled || !chosen} onClick={add}>添加</button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A set of values the plugin offers, as checkboxes, each by its name.
 *
 * What is stored is the list the plugin reads, and only the person moves
 * anything in it: checking a value adds it at the end, unchecking takes that
 * value out, and nothing else changes place. Where the plugin reads the list in
 * order, the checked ones come first, in the stored order, and move up and down
 * -- top to bottom on the page is the order saved. Otherwise the values stay in
 * the order they were declared, and the stored order is left as it was.
 *
 * A stored value the declaration does not name -- one since withdrawn, or a
 * model since disconnected -- stays where it is, says it is no longer offered,
 * and can be taken out. This control never drops what it cannot name.
 */
function CheckListField({
  field,
  list,
  value,
  onChange,
  describedBy,
}: {
  field: PanelField;
  list: CheckList;
  value: string[];
  onChange: (next: string[]) => void;
  describedBy?: string;
}) {
  const disabled = useContext(FormDisabled);
  const named = new Map(list.options.map((option) => [option.value, option.label]));
  // Rows are keyed by position, so a move changes what two rows say rather
  // than moving a focused control out from under the keyboard. The focus then
  // goes where the value went -- the row it moved to, or, when checking moves
  // it between the checked and the rest, its own box -- so the next key press
  // acts on the same value, not on whichever one slid into its place.
  const moves = useRef<(HTMLButtonElement | null)[]>([]);
  const boxes = useRef(new Map<string, HTMLButtonElement>());
  const [follow, setFollow] = useState<{ index: number; up: boolean } | { value: string } | null>(null);
  useEffect(() => {
    if (!follow) return;
    if ("value" in follow) {
      boxes.current.get(follow.value)?.focus();
    } else {
      const same = moves.current[follow.index * 2 + (follow.up ? 0 : 1)];
      const other = moves.current[follow.index * 2 + (follow.up ? 1 : 0)];
      (same && !same.disabled ? same : other)?.focus();
    }
    setFollow(null);
  }, [follow]);
  const check = (key: string, on: boolean) => {
    onChange(on ? [...value, key] : value.filter((item) => item !== key));
    if (list.ordered) setFollow({ value: key });
  };
  const move = (index: number, up: boolean) => {
    const to = up ? index - 1 : index + 1;
    const next = [...value];
    [next[index], next[to]] = [next[to], next[index]];
    onChange(next);
    setFollow({ index: to, up });
  };
  const box = (key: string, label: string, checked: boolean) => (
    <Checkbox className="check-list-name" label={label} name={field.key} checked={checked} disabled={disabled}
      onCheckedChange={(next) => check(key, next === true)}
      ref={(node) => { if (node) boxes.current.set(key, node); }} />
  );
  // Without a name there is nothing to call it but that: the stored key is
  // the machine's word for it, and this page does not print one.
  const gone = (key: string, index: number) => (
    <li className="check-list-row is-gone" key={`stored-${index}`}>
      <span className="check-list-name">已不再提供的一项</span>
      <button type="button" className="btn btn-quiet" data-testid={`checks-${field.key}-remove-${key}`}
        onClick={() => onChange(value.filter((_, i) => i !== index))}>移除</button>
    </li>
  );
  const nothing = !list.options.length && !value.length;
  if (list.ordered) {
    const unchecked = list.options.filter((option) => !value.includes(option.value));
    return (
      <div className="check-list is-ordered" data-testid={`checks-${field.key}`} role="group"
        aria-label={field.label} aria-describedby={describedBy}>
        {value.length ? (
          <ol className="check-list-rows">
            {value.map((key, index) => {
              const label = named.get(key);
              if (label === undefined) return gone(key, index);
              return (
                <li className="check-list-row" key={`stored-${index}`}>
                  {box(key, label, true)}
                  <span className="check-list-moves">
                    <button type="button" className="btn btn-quiet" disabled={index === 0}
                      ref={(node) => { moves.current[index * 2] = node; }}
                      aria-label={`上移 ${label}`} title="上移" data-testid={`checks-${field.key}-up-${key}`}
                      onClick={() => move(index, true)}><Icon name="up" size={16} /></button>
                    <button type="button" className="btn btn-quiet" disabled={index === value.length - 1}
                      ref={(node) => { moves.current[index * 2 + 1] = node; }}
                      aria-label={`下移 ${label}`} title="下移" data-testid={`checks-${field.key}-down-${key}`}
                      onClick={() => move(index, false)}><Icon name="down" size={16} /></button>
                  </span>
                </li>
              );
            })}
          </ol>
        ) : null}
        {unchecked.length ? (
          <ul className="check-list-rows">
            {unchecked.map((option) => (
              <li className="check-list-row" key={option.value}>{box(option.value, option.label, false)}</li>
            ))}
          </ul>
        ) : null}
        {nothing ? <p className="micro faint">还没有一项。</p> : null}
      </div>
    );
  }
  const unknown = value.flatMap((key, index) => named.has(key) ? [] : [{ key, index }]);
  return (
    <div className="check-list" data-testid={`checks-${field.key}`} role="group"
      aria-label={field.label} aria-describedby={describedBy}>
      {nothing ? <p className="micro faint">还没有一项。</p> : (
        <ul className="check-list-rows">
          {list.options.map((option) => (
            <li className="check-list-row" key={option.value}>
              {box(option.value, option.label, value.includes(option.value))}
            </li>
          ))}
          {unknown.map(({ key, index }) => gone(key, index))}
        </ul>
      )}
    </div>
  );
}

/**
 * `type: "list"` (CHARTER §7.113): rows a person can add and remove.
 *
 * The contract has one list type for two shapes, and both are in the product.
 * With `item_fields` a row is an object and each row is a set of named controls
 * -- `housekeeping`'s cleanup rules. Without them a row is a plain value and
 * each row is one box: the generated settings pages have thirteen of those against three
 * of the other kind, and `housekeeping` has both at once, an `include` list of
 * wildcards inside every rule row.
 *
 * Rows are addressed by index rather than by a key of their own: a row here has
 * no identity until it is submitted, and inventing one would put a field in the
 * object the plugin did not declare. A table that does declare one (a `hidden`
 * field, its fixed row id) has it carried inside the row like any other value:
 * not drawn, kept by `setCell`, empty on a new row until the table is stored.
 */
function ListField({
  field,
  idPrefix,
  rows,
  onChange,
  primaryKeys,
  describedBy,
}: {
  field: PanelField;
  idPrefix: string;
  rows: unknown[];
  onChange: (rows: unknown[]) => void;
  primaryKeys?: string[];
  describedBy?: string;
}) {
  const items = field.item_fields ?? [];
  const [advancedRows, setAdvancedRows] = useState<string[]>([]);
  // A hidden field (a table's fixed row id) is not drawn but stays in the row: `setCell` keeps what it
  // does not touch, and a new row starts with it empty for the one who stores the table to fill in.
  const drawn = items.filter((item) => !item.hidden);
  const setCell = (index: number, key: string, value: unknown) =>
    onChange(
      rows.map((row, i) =>
        i === index
          ? { ...(row as Record<string, unknown>), [key]: value }
          : row,
      ),
    );
  const setValue = (index: number, value: unknown) =>
    onChange(rows.map((row, i) => (i === index ? value : row)));

  /** One box for a list of plain values, carrying over what the field said. */
  const plain: PanelField = {
    key: field.key,
    label: field.label,
    type: "string",
    secret: field.secret,
    placeholder: field.placeholder,
  };

  return (
    <div className="list-field" data-testid={`list-${field.key}`} role="group" aria-label={field.label}
      data-state={rows.length ? "filled" : "empty"} aria-describedby={items.length ? describedBy : undefined}>
      {rows.length === 0 ? (
        <p className="micro faint">尚未添加</p>
      ) : (
        <ol className="list-rows">
          {rows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li className="list-row" key={index}>
              <div className="list-row-fields">
                {items.length === 0 ? (
                  <div className="list-cell">
                    <label
                      className="field-label micro"
                      htmlFor={`${idPrefix}-${index}`}
                    >
                      第 {index + 1} 项
                    </label>
                    <FieldControl
                      field={plain}
                      id={`${idPrefix}-${index}`}
                      value={row}
                      onChange={(v) => setValue(index, v)}
                      describedBy={describedBy}
                    />
                  </div>
                ) : (
                  [drawn.filter(item => item.required || (primaryKeys ? primaryKeys.includes(item.key) : !item.advanced)),
                    drawn.filter(item => !item.required && (primaryKeys ? !primaryKeys.includes(item.key) : item.advanced))].map((group, groupIndex) => {
                    const controls = group.map((item) => {
                    const cellValue = (row as Record<string, unknown>)?.[
                      item.key
                    ];
                    return (
                      <div className={`list-cell${["choice", "number", "boolean"].includes(item.type ?? "") && !item.writable ? " form-field-short" : ""}`} key={item.key} data-field-key={item.key}>
                        <label
                          className="field-label micro"
                          htmlFor={`${idPrefix}-${index}-${item.key}`}
                        >
                          {item.label}
                          {item.required ? (
                            <span className="req" aria-hidden="true">
                              {" "}
                              *
                            </span>
                          ) : null}
                        </label>
                        {/* A cell may itself be a list of plain values, which is
                            the one level of nesting the contract allows and
                            `housekeeping` uses for each rule's `include`
                            wildcards. Sent through FieldControl it came out as
                            a text box, and typing into it wrote a string where
                            an array belongs. */}
                        {item.type === "list" ? (
                          <ListField
                            field={item}
                            idPrefix={`${idPrefix}-${index}-${item.key}`}
                            rows={
                              Array.isArray(cellValue)
                                ? (cellValue as unknown[])
                                : []
                            }
                            onChange={(next) => setCell(index, item.key, next)}
                            describedBy={!item.secret && item.placeholder ? `${idPrefix}-${index}-${item.key}-help` : undefined}
                          />
                        ) : (
                          <FieldControl
                            field={item}
                            id={`${idPrefix}-${index}-${item.key}`}
                            value={cellValue ?? emptyValue(item)}
                            onChange={(v) => setCell(index, item.key, v)}
                            describedBy={!item.secret && item.placeholder ? `${idPrefix}-${index}-${item.key}-help` : undefined}
                          />
                        )}
                        {!item.secret && item.placeholder ? <p className="field-help micro" id={`${idPrefix}-${index}-${item.key}-help`}>{item.placeholder}</p> : null}
                      </div>
                    );
                    });
                    return groupIndex === 0 ? controls : group.length ? <Accordion className="list-advanced" key="advanced" forceMount
                      value={advancedRows} onValueChange={setAdvancedRows} items={[{ value: String(index), title: `高级参数（${group.length}）`,
                        content: <div className="list-row-fields">{controls}</div> }]} /> : null;
                  })
                )}
              </div>
              <button
                type="button"
                className="btn btn-quiet list-remove"
                data-testid={`list-${field.key}-remove-${index}`}
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
              >
                {`删掉第 ${index + 1} 行`}
              </button>
            </li>
          ))}
        </ol>
      )}
      <div className="list-actions">
        <button
          type="button"
          className="btn"
          data-testid={`list-${field.key}-add`}
          onClick={() =>
            onChange([...rows, items.length === 0 ? "" : emptyRow(items)])
          }
        >
          添加
        </button>
      </div>
    </div>
  );
}
